import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./style.scss";
import axios from "axios";
import Navbar from "../../components/navbar/Navbar";
import { AuthContext } from "../../context/authContext/AuthContext";
import { io } from "socket.io-client";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";

const NSFW_WORDS = [
  "porn","xxx","sex","rape","incest","nsfw","nude","naked",
  "fuck","shit","bitch","asshole","dick","pussy"
];
const isNSFW = (t="") => NSFW_WORDS.some(w => (t||"").toLowerCase().includes(w));

const Doubts = () => {
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState("");
  const [warn, setWarn] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onlineMap, setOnlineMap] = useState({}); // userId -> true
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [navBottom, setNavBottom] = useState(96);
  const [viewportOffset, setViewportOffset] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoDeleteMinutes, setAutoDeleteMinutes] = useState(null);
  const [autoDeleteUnit, setAutoDeleteUnit] = useState("minutes");
  const [autoDeleteValue, setAutoDeleteValue] = useState("");
  const [cleanupInterval, setCleanupInterval] = useState("30");
  const [lastDeleteEventTime, setLastDeleteEventTime] = useState(0);
  const listRef = useRef(null);
  const socketRef = useRef(null);
  const selectedRef = useRef(null);
  const markReadTimeoutRef = useRef(null);
  const pendingReadConvoRef = useRef(null);
  const messageCacheRef = useRef(new Map()); // convoId -> messages[]
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Unread storage key and helpers (for navbar dot)
  const UNREAD_KEY = useMemo(() => (user?._id ? `chat_unread_${user._id}` : null), [user?._id]);
  const readUnread = () => {
    if (!UNREAD_KEY) return [];
    try { return JSON.parse(localStorage.getItem(UNREAD_KEY) || "[]"); } catch { return []; }
  };
  const writeUnread = (arr) => {
    if (!UNREAD_KEY) return;
    try { localStorage.setItem(UNREAD_KEY, JSON.stringify(Array.from(new Set(arr.map(String))))); } catch(_) {}
    // Notify navbar in same tab
    try { window.dispatchEvent(new Event('chat-unread-changed')); } catch(_) {}
  };
  const addUnread = (convoId) => {
    const cur = readUnread();
    if (!cur.includes(String(convoId))) {
      cur.push(String(convoId));
      writeUnread(cur);
    }
  };
  const clearUnread = (convoId) => {
    const cur = readUnread();
    const next = cur.filter((id) => String(id) !== String(convoId));
    writeUnread(next);
  };

  // Keep selectedRef in sync with selected state
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const authHeader = useMemo(() => ({ headers: { token: "Bearer " + user?.accessToken } }), [user?.accessToken]);
  const isMobile = useMemo(() => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768, []);

  // Initialize socket + presence + global event listeners
  useEffect(() => {
    const URL = process.env.REACT_APP_PROXY_URL || window.location.origin;
    // Force polling for mobile devices (more reliable)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const transports = isMobile ? ["polling", "websocket"] : ["websocket", "polling"];
    
    const s = io(URL, { 
      transports,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10
    });
    socketRef.current = s;
    
    s.on("connect", () => { 
      console.log('[socket] Connected, ID:', s.id, 'User:', user?._id, 'Role:', user?.isTeacher ? 'teacher' : 'student');
      if (user?._id) {
        s.emit("presence:init", user._id);
        // Send initial heartbeat too for mobile reliability
        s.emit('presence:ping', user._id);
      }
    });
    
    s.on("disconnect", () => {
      console.log('[socket] Disconnected');
    });
    
    s.on("connect_error", (err) => {
      console.error('[socket] Connection error:', err);
    });
    
    s.on("presence:update", (p) => {
      console.log('[socket] 👤 Presence update:', p.userId, p.online ? '🟢 online' : '🔴 offline', p.lastSeen ? `lastSeen: ${p.lastSeen}` : '');
      setOnlineMap(prev => {
        const clone = { ...prev };
        if (p.online) clone[p.userId] = true; else delete clone[p.userId];
        return clone;
      });
      // Always refresh stored lastSeen if provided (useful for mobile)
      if (p.lastSeen) {
        setConversations(prev => prev.map(c => String(c.other?._id) === String(p.userId) ? { ...c, other: { ...c.other, lastSeen: p.lastSeen } } : c));
        if (String(selected?.other?._id) === String(p.userId)) setSelected(cur => ({ ...cur, other: { ...cur.other, lastSeen: p.lastSeen } }));
      }
    });
    
    // Register global chat event listeners
    console.log('[socket] Registering global chat event listeners');
    
    const messageHandler = (msg) => {
      console.log('[socket] Received chat:message event:', msg);
      // Update lastMessage in conversation list for all conversations
      setConversations((prev)=> prev.map(c=> String(c._id)===String(msg.conversationId)? { ...c, lastMessage: msg.text, updatedAt: new Date().toISOString() }: c));
      
      // Only add to messages if it's the active conversation (using ref for latest value)
  if (String(msg.conversationId) === String(selectedRef.current?._id)) {
        setMessages((m) => {
          const isDuplicate = m.some(x => {
            if (String(x._id) === String(msg._id)) return true;
            if (String(msg.sender) === String(user?._id) && x.text === msg.text) {
              const timeDiff = Math.abs(new Date(msg.createdAt) - new Date(x.createdAt));
              if (timeDiff < 2000) return true;
            }
            return false;
          });
          if (isDuplicate) return m;
          const next = [...m, msg];
          // keep cache in sync for active conversation
          try { messageCacheRef.current.set(String(msg.conversationId), next); } catch(_) {}
          return next;
        });

        // If this message is to me and I am viewing this conversation, mark as read immediately (debounced)
        if (String(msg.recipient) === String(user?._id)) {
          try {
            // debounce rapid bursts
            if (markReadTimeoutRef.current) clearTimeout(markReadTimeoutRef.current);
            pendingReadConvoRef.current = String(msg.conversationId);
            markReadTimeoutRef.current = setTimeout(async () => {
              const convoId = pendingReadConvoRef.current;
              if (!convoId) return;
              console.log('[chat] ✓ Scheduling READ for active conversation:', convoId);
              try {
                await axios.post("/api/chat/messages/read", { conversationId: String(convoId) }, authHeader);
                console.log('[chat] ✓ Sent READ for active conversation');
                // Clear unread badge for this conversation
                clearUnread(convoId);
              } catch (e) {
                console.error('[chat] ❌ Failed to send READ for active conversation:', e.message);
              } finally {
                markReadTimeoutRef.current = null;
              }
            }, 150);
          } catch (err) {
            console.error('[chat] ❌ Debounce/read error:', err?.message || err);
          }
        }
      } else {
        // Not the active conversation: if it's to me, mark as unread for navbar
        if (String(msg.recipient) === String(user?._id)) {
          addUnread(msg.conversationId);
        }
      }
    };
    
    const readHandler = (payload) => {
      if (!payload || !payload.messageIds) return;
      setMessages(m => m.map(msg => payload.messageIds.includes(msg._id) ? { ...msg, seen: true, seenAt: msg.seenAt || new Date().toISOString() } : msg));
    };
    
    const deleteHandler = (payload) => {
      console.log('[socket] 🗑️ DELETE event:', payload?.conversationId, 'messages:', payload?.messageIds?.length);
      setLastDeleteEventTime(Date.now());
      if (!payload || !payload.messageIds) {
        console.log('[socket] ❌ Invalid delete payload');
        return;
      }
      
      const deletedIds = payload.messageIds.map(id => String(id));
      
      // Remove deleted messages from current conversation
      setMessages(currentMessages => {
        const filtered = currentMessages.filter(msg => !deletedIds.includes(String(msg._id)));
        if (filtered.length !== currentMessages.length) {
          console.log(`[socket] ✅ Deleted ${currentMessages.length - filtered.length} messages (${currentMessages.length} → ${filtered.length})`);
        }
        // sync cache for active conversation
        if (selectedRef.current?._id) {
          try { messageCacheRef.current.set(String(selectedRef.current._id), filtered); } catch(_) {}
        }
        return filtered;
      });
      
      // Reload conversations to get accurate lastMessage from server
      console.log('[socket] 🔄 Reloading conversations for sidebar update');
      try {
        loadConversations();
      } catch (e) {
        console.error('[socket] ❌ Failed to reload conversations:', e.message);
      }
    };
    
    // Also listen for auto-delete config updates to refresh note without page reload
    const onConfigUpdate = (cfg) => {
      try {
        const minutes = cfg?.minutes ?? null;
        const intervalSeconds = cfg?.intervalSeconds ?? 30;
        setAutoDeleteMinutes(minutes);
        setCleanupInterval(String(intervalSeconds));
        // Keep settings dialog values coherent
        if (minutes && minutes >= 60) {
          setAutoDeleteUnit("hours");
          setAutoDeleteValue(String(Math.floor(minutes/60)));
        } else if (minutes) {
          setAutoDeleteUnit("minutes");
          setAutoDeleteValue(String(minutes));
        } else {
          setAutoDeleteValue("");
        }
        console.log('[socket] ⚙️ Auto-delete config updated via socket:', minutes, 'min, every', intervalSeconds, 's');
      } catch(_) {}
    };

    // Listen to both event types for reliability
    s.on("chat:message", messageHandler);
    s.on("chat:read", readHandler);
    s.on("chat:messages-deleted", deleteHandler);
    s.on("chat:delete", deleteHandler);
    s.on("config:chat-auto-delete", onConfigUpdate);
    
    console.log('[socket] All event listeners registered');
    
    return () => { 
      s.off("chat:message", messageHandler);
      s.off("chat:read", readHandler);
      s.off("chat:messages-deleted", deleteHandler);
      s.off("chat:delete", deleteHandler);
      s.off("config:chat-auto-delete", onConfigUpdate);
      try { s.disconnect(); } catch(_){} 
    };
  }, [user?._id]);

  // Presence heartbeat (helps mobile keep online status current)
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !user?._id) return;
    const interval = setInterval(() => {
      try {
        s.emit('presence:ping', user._id);
      } catch(_) {}
    }, 20000); // every 20s
    return () => clearInterval(interval);
  }, [user?._id]);

  // Re-emit presence when tab becomes visible (mobile Safari/background cases)
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !user?._id) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        try {
          s.emit('presence:init', user._id);
          s.emit('presence:ping', user._id);
        } catch(_) {}
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [user?._id]);

  // Cleanup any pending read timers on unmount
  useEffect(() => {
    return () => {
      if (markReadTimeoutRef.current) clearTimeout(markReadTimeoutRef.current);
    };
  }, []);

  // Join/leave room when conversation changes
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !selected?._id) return;
    
    s.emit("chat:join", String(selected._id));
    return () => {
      s.emit("chat:leave", String(selected._id));
    };
  }, [selected?._id]);

  const loadConversations = async () => {
    try {
      console.log('[api] 📡 Loading conversations...');
      const res = await axios.get("/api/chat/conversations", authHeader);
      setConversations(res.data || []);
      console.log('[api] ✅ Loaded', res.data?.length || 0, 'conversations');
    } catch (e) {
      console.error('[api] ❌ Failed to load conversations:', e.message);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      console.log('[api] 📡 Loading messages for conversation:', conversationId);
      const res = await axios.get(`/api/chat/messages/${conversationId}`, authHeader);
      const data = res.data || [];
      setMessages(data);
      try { messageCacheRef.current.set(String(conversationId), data); } catch(_) {}
      console.log('[api] ✅ Loaded', res.data?.length || 0, 'messages');
      // scroll to bottom
      setTimeout(() => { listRef.current?.scrollTo(0, listRef.current.scrollHeight); }, 50);
      return data;
    } catch (e) {
      console.error('[api] ❌ Failed to load messages:', e.message);
      return [];
    }
  };

  // Periodically refresh messages to catch deletions (fallback for mobile socket issues)
  useEffect(() => {
    if (!selected?._id || !autoDeleteMinutes) return;
    
    // Check every 5 seconds for faster sync (only when auto-delete is enabled)
    const interval = setInterval(() => {
      const timeSinceLastDelete = Date.now() - lastDeleteEventTime;
      // Skip polling if socket event received in last 10 seconds
      if (timeSinceLastDelete < 10000) {
        console.log('[polling] Skipping - socket event received recently');
        return;
      }
      console.log('[polling] Refreshing messages as fallback...');
      // Only refresh if socket is NOT properly receiving events (fallback)
      loadMessages(selected._id);
      loadConversations(); // Also refresh sidebar to update last message
    }, 5000); // 5 seconds for near real-time on mobile
    
    return () => clearInterval(interval);
  }, [selected?._id, autoDeleteMinutes, lastDeleteEventTime]);

  // Mobile-only: periodic read-receipt fallback while a chat is open
  useEffect(() => {
    if (!isMobile || !selected?._id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        await axios.post('/api/chat/messages/read', { conversationId: String(selected._id) }, authHeader);
      } catch(_) {}
    };
    const id = setInterval(() => { if (!cancelled && document.visibilityState === 'visible') tick(); }, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isMobile, selected?._id, authHeader]);

  // Mobile-only: fallback presence/lastSeen refresher for the selected conversation counterpart
  useEffect(() => {
    if (!isMobile || !selected?.other?._id) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await axios.get(`/api/user/${selected.other._id}`, authHeader);
        const lastSeen = res.data?.lastSeen;
        if (lastSeen) {
          setConversations(prev => prev.map(c => c._id === selected._id ? { ...c, other: { ...c.other, lastSeen } } : c));
          // If lastSeen within 60s, consider online (soft fallback)
          const recent = Date.now() - new Date(lastSeen).getTime() < 60000;
          setOnlineMap(prev => {
            const clone = { ...prev };
            if (recent) clone[selected.other._id] = true; else delete clone[selected.other._id];
            return clone;
          });
        }
      } catch(_) {}
    };
    const id = setInterval(() => { if (!cancelled && document.visibilityState === 'visible') pull(); }, 12000);
    // Initial pull shortly after open
    setTimeout(() => { if (!cancelled) pull(); }, 800);
    return () => { cancelled = true; clearInterval(id); };
  }, [isMobile, selected?._id, selected?.other?._id, authHeader]);

  // Periodically refresh conversations list as a safety net (handles sidebar not updating on some mobiles)
  useEffect(() => {
    if (!autoDeleteMinutes) return; // only when auto-delete is enabled
    const interval = setInterval(() => {
      console.log('[polling] Refreshing conversations list (safety net)');
      loadConversations();
    }, 12000); // 12s to keep bandwidth low
    return () => clearInterval(interval);
  }, [autoDeleteMinutes]);

  const loadTeachers = async () => {
    try {
      console.log('[api] 📡 Loading teachers...');
      const res = await axios.get("/api/user?role=teacher", authHeader);
      setTeachers(res.data || []);
      console.log('[api] ✅ Loaded', res.data?.length || 0, 'teachers');
    } catch (e) {
      console.error('[api] ❌ Failed to load teachers:', e.message);
    }
  };
  const loadStudents = async () => {
    try {
      console.log('[api] 📡 Loading students...');
      const res = await axios.get("/api/user?role=student", authHeader);
      setStudents(res.data || []);
      console.log('[api] ✅ Loaded', res.data?.length || 0, 'students');
    } catch (e) {
      console.error('[api] ❌ Failed to load students:', e.message);
    }
  };

  const openConversation = async (convo) => {
    console.log('[chat] 📂 Opening conversation:', convo._id, 'with:', convo.other?.fullname);
    setSelected(convo);
    // Show cached messages immediately to avoid flicker
    const cached = messageCacheRef.current.get(String(convo._id));
    if (cached) {
      setMessages(cached);
      setMessagesLoading(true);
      // background refresh to get latest
      loadMessages(convo._id).finally(() => setMessagesLoading(false));
    } else {
      // Show placeholder while loading, not previous chat
      setMessages([]);
      setMessagesLoading(true);
      await loadMessages(convo._id);
      setMessagesLoading(false);
    }
    
    // Mark unseen messages as read and wait for response
    try { 
      const response = await axios.post("/api/chat/messages/read", { conversationId: String(convo._id) }, authHeader);
      console.log('[chat] ✓ Marked messages as read:', response.data?.messageIds?.length || 0, 'messages');
      // Clear unread badge for this conversation
      clearUnread(convo._id);
    } catch(e) {
      console.error('[chat] ❌ Failed to mark messages as read:', e.message);
    }
  };

  const startChatWith = async (otherUserId) => {
    try {
      setLoading(true);
      const res = await axios.post(`/api/chat/conversation/with/${otherUserId}`, {}, authHeader);
      setShowNewChat(false);
      await loadConversations();
      const created = res.data;
      const convo = created || {};
      // attach other brief if missing
      let other = null;
      if (!user?.isTeacher) other = teachers.find(t => t._id === otherUserId);
      else other = students.find(s => s._id === otherUserId);
      openConversation({ ...convo, other: { _id: otherUserId, fullname: other?.fullname, profilePic: other?.profilePic, isTeacher: other?.isTeacher } });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !selected) return;
    if (isNSFW(text)) { setWarn("NSFW Chat not allowed"); return; }
    setWarn("");
    
    // Optimistic update - add message immediately to UI
    const tempMsg = {
      _id: 'temp-' + Date.now(),
      conversationId: selected._id,
      sender: user._id,
      text,
      createdAt: new Date().toISOString(),
      seen: false,
    };
    setMessages((m) => {
      const next = [...m, tempMsg];
      try { messageCacheRef.current.set(String(selected._id), next); } catch(_) {}
      return next;
    });
    setInput("");
    setConversations((prev)=> prev.map(c=> c._id===selected._id? { ...c, lastMessage: text, updatedAt: new Date().toISOString() }: c));
    
    // Scroll immediately
    setTimeout(() => { listRef.current?.scrollTo(0, listRef.current.scrollHeight); }, 50);
    
    try {
      const res = await axios.post("/api/chat/messages", { conversationId: selected._id, text }, authHeader);
      // Replace temp message with real one from server
      if (res.data) {
        setMessages((m) => {
          const next = m.map(msg => msg._id === tempMsg._id ? res.data : msg);
          try { messageCacheRef.current.set(String(selected._id), next); } catch(_) {}
          return next;
        });
      }
    } catch (e) {
      // Remove temp message on error
      setMessages((m) => {
        const next = m.filter(msg => msg._id !== tempMsg._id);
        try { messageCacheRef.current.set(String(selected._id), next); } catch(_) {}
        return next;
      });
      const code = e?.response?.data?.code;
      if (code === "NSFW_BLOCKED") setWarn("NSFW Chat not allowed");
      else setWarn("Failed to send message");
    }
  };

  const loadAutoDeleteSettings = async () => {
    try {
      const res = await axios.get("/api/config/chat-auto-delete", authHeader);
      const mins = res.data?.minutes;
      const intervalSec = res.data?.intervalSeconds || 30;
      setAutoDeleteMinutes(mins);
      setCleanupInterval(String(intervalSec));
      if (mins) {
        // Convert to user-friendly format
        if (mins >= 60) {
          setAutoDeleteUnit("hours");
          setAutoDeleteValue(String(Math.floor(mins / 60)));
        } else {
          setAutoDeleteUnit("minutes");
          setAutoDeleteValue(String(mins));
        }
      } else {
        setAutoDeleteValue("");
      }
    } catch (err) {
      console.error("Failed to load auto-delete settings:", err);
    }
  };

  const saveAutoDeleteSettings = async () => {
    try {
      let minutes = null;
      const val = parseInt(autoDeleteValue);
      if (!isNaN(val) && val > 0) {
        minutes = autoDeleteUnit === "hours" ? val * 60 : val;
      }
      const intervalSeconds = parseInt(cleanupInterval) || 30;
      await axios.put("/api/config/chat-auto-delete", { minutes, intervalSeconds }, authHeader);
      setAutoDeleteMinutes(minutes);
      setSettingsOpen(false);
      alert(minutes ? `Chat auto-delete enabled: ${autoDeleteValue} ${autoDeleteUnit} (checks every ${intervalSeconds}s)` : "Chat auto-delete disabled");
    } catch (err) {
      alert("Failed to save settings");
    }
  };

  useEffect(() => {
    console.log('[app] 🚀 Initializing Doubts page, user role:', user?.isTeacher ? 'teacher' : 'student');
    loadConversations();
    loadAutoDeleteSettings(); // Load for both teacher and student
    
    // Preload users immediately to avoid delay
    if (!user?.isTeacher) {
      console.log('[app] 📥 Preloading teachers list');
      loadTeachers();
    } else {
      console.log('[app] 📥 Preloading students list');
      loadStudents();
    }
  }, [user?.isTeacher]);

  // Mobile + visibility fallback: keep auto-delete note in sync without refresh
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await axios.get("/api/config/chat-auto-delete", authHeader);
        if (cancelled) return;
        const minutes = res.data?.minutes ?? null;
        const intervalSeconds = res.data?.intervalSeconds ?? 30;
        setAutoDeleteMinutes(minutes);
        setCleanupInterval(String(intervalSeconds));
        if (minutes && minutes >= 60) {
          setAutoDeleteUnit("hours");
          setAutoDeleteValue(String(Math.floor(minutes/60)));
        } else if (minutes) {
          setAutoDeleteUnit("minutes");
          setAutoDeleteValue(String(minutes));
        } else {
          setAutoDeleteValue("");
        }
      } catch(_) {}
    };
    const onVis = () => { if (document.visibilityState === 'visible') pull(); };
    window.addEventListener('focus', onVis);
    document.addEventListener('visibilitychange', onVis);
    // Mobile periodic fallback
    const id = isMobile ? setInterval(pull, 12000) : null;
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onVis);
      document.removeEventListener('visibilitychange', onVis);
      if (id) clearInterval(id);
    };
  }, [authHeader, isMobile]);

  const isMine = (m) => String(m.sender) === String(user?._id);
  const isSeen = (m) => !!m.seen;
  const formatLastSeen = (d) => {
    if (!d) return "";
    const date = new Date(d);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // Fallback to full date for older timestamps
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Always scroll to latest message when messages change in an open conversation
  useEffect(() => {
    if (selected && listRef.current) {
      const el = listRef.current;
      // Attempt multiple times to account for images or fonts loading
      const scrollBottom = () => { el.scrollTop = el.scrollHeight; };
      scrollBottom();
      const t1 = setTimeout(scrollBottom, 50);
      const t2 = setTimeout(scrollBottom, 120);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [messages.length, selected?._id]);

  // Keyboard + viewport awareness (mobile only)
  useEffect(() => {
    // Only enable on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    if (!isMobile) return;
    
    // Store initial navbar bottom position (only calculate once)
    let initialNavBottom = 96;
    const navEl = document.querySelector('.navbar');
    if (navEl) {
      const rect = navEl.getBoundingClientRect();
      initialNavBottom = Math.round(rect.bottom);
      setNavBottom(initialNavBottom);
    }
    
    const check = () => {
      const screenH = window.screen?.height || window.innerHeight;
      if (window.visualViewport) {
        const { height, offsetTop } = window.visualViewport;
        const shrunk = height < screenH * 0.85;
        const isInputActive = document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA');
        setKeyboardOpen(shrunk && isInputActive);
        setViewportOffset(offsetTop || 0);
        setViewportHeight(height);
      } else {
        const shrunk = window.innerHeight < screenH - 180;
        const isInputActive = document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA');
        setKeyboardOpen(shrunk && isInputActive);
        setViewportOffset(0);
        setViewportHeight(window.innerHeight);
      }
    };
    const onResize = () => { check(); };
    // init
    onResize();

    window.addEventListener('resize', onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
      window.visualViewport.addEventListener('scroll', onResize);
    }
    const inputEl = document.querySelector('.chat-input input');
    const onFocus = () => {
      setTimeout(() => {
        onResize();
        // Scroll input into view
        if (inputEl) {
          inputEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    };
    const onBlur = () => setTimeout(onResize, 120);
    if (inputEl) {
      inputEl.addEventListener('focus', onFocus);
      inputEl.addEventListener('blur', onBlur);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize);
        window.visualViewport.removeEventListener('scroll', onResize);
      }
      if (inputEl) {
        inputEl.removeEventListener('focus', onFocus);
        inputEl.removeEventListener('blur', onBlur);
      }
    };
  }, [selected?._id]);

  // Lock body scroll when keyboard is open
  useEffect(() => {
    document.body.style.overflow = keyboardOpen ? 'hidden' : '';
    document.body.style.position = keyboardOpen ? 'fixed' : '';
    document.body.style.width = keyboardOpen ? '100%' : '';
    document.body.style.height = keyboardOpen ? '100vh' : '';
    return () => { 
      document.body.style.overflow = ''; 
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [keyboardOpen]);

  // Keep scroller at bottom when keyboard opens so latest message + input are visible
  useEffect(() => {
    if (keyboardOpen && listRef.current) {
      const el = listRef.current;
      const scrollBottom = () => { 
        el.scrollTop = el.scrollHeight;
        // Also ensure input is visible
        const inputEl = document.querySelector('.chat-input input');
        if (inputEl) {
          // Small delay to ensure keyboard is fully rendered
          setTimeout(() => {
            inputEl.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
          }, 200);
        }
      };
      const t1 = setTimeout(scrollBottom, 50);
      const t2 = setTimeout(scrollBottom, 150);
      const t3 = setTimeout(scrollBottom, 300);
      return () => { 
        clearTimeout(t1); 
        clearTimeout(t2); 
        clearTimeout(t3);
      };
    }
  }, [keyboardOpen, selected?._id]);

  return (
    <div
      className={`chat-page ${keyboardOpen ? 'keyboard-active' : ''}`}
      style={{
        "--nav-bottom": `${navBottom}px`,
        "--viewport-offset": `${viewportOffset}px`,
        "--viewport-height": `${viewportHeight}px`
      }}
    >
      <Navbar />
      <div className="chat-container">
        {/* Left: Conversation list */}
        <div className="chat-list">
          <div className="header">
            <h3>{user?.isTeacher ? "Student Messages" : "Chats"}</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {user?.isTeacher && (
                <button className="settings-btn" onClick={() => setSettingsOpen(true)} title="Chat Settings">
                  <SettingsIcon style={{ fontSize: 20 }} />
                </button>
              )}
              <button className="new-chat" onClick={()=> setShowNewChat((s)=>!s)}>+ New</button>
            </div>
          </div>

          {showNewChat && (
            <div className="new-chat-panel">
              <div className="panel-title">{user?.isTeacher ? "Start a chat with a Student" : "Start a chat with a Teacher"}</div>
              <div className="teachers">
                {(user?.isTeacher ? students : teachers).map((p)=> (
                  <button key={p._id} disabled={loading} onClick={()=> startChatWith(p._id)} className="teacher-item">
                    <img src={p.profilePic || "/logo.png"} alt="avatar" />
                    <span>{p.fullname}</span>
                  </button>
                ))}
                {(user?.isTeacher ? students : teachers).length===0 && (
                  <div className="empty">{user?.isTeacher ? "No students found" : "No teachers found"}</div>
                )}
              </div>
            </div>
          )}

          <div className="items">
            {conversations.map((c)=> (
              <button key={c._id} className={`item ${selected?._id===c._id? 'active':''}`} onClick={()=> openConversation(c)}>
                <div style={{ position:'relative' }}>
                  <img src={c.other?.profilePic || "/logo.png"} alt="avatar" />
                  {onlineMap[String(c.other?._id)] && <div className="online-dot" />}
                </div>
                <div className="meta">
                  <div className="name">{c.other?.fullname || (c.other?.isTeacher? 'Teacher' : 'Student')}</div>
                  <div className="last">{c.lastMessage || ""}</div>
                  {!onlineMap[String(c.other?._id)] && c.other?.lastSeen && (
                    <div className="last-seen">Last: {formatLastSeen(c.other.lastSeen)}</div>
                  )}
                </div>
              </button>
            ))}
            {conversations.length===0 && <div className="empty">No conversations yet</div>}
          </div>
        </div>

        {/* Right: Chat window */}
        <div className={`chat-window ${selected? 'open':''} ${keyboardOpen && selected ? 'keyboard-open' : ''}`}>
          {selected ? (
            <>
              <div className="chat-header">
                <div className="back" onClick={()=> setSelected(null)}>&larr;</div>
                <div style={{ position:'relative' }}>
                  <img src={selected.other?.profilePic || "/logo.png"} alt="avatar" />
                  {onlineMap[String(selected.other?._id)] && <div className="online-dot" />}
                </div>
                <div className="who">
                  <div className="name">{selected.other?.fullname}</div>
                  <div className="role">
                    {selected.other?.isTeacher? 'Teacher' : 'Student'}
                    {onlineMap[String(selected.other?._id)] ? ' • Online' : selected.other?.lastSeen ? ` • Last seen ${formatLastSeen(selected.other.lastSeen)}` : ''}
                  </div>
                </div>
              </div>
              <div className="chat-messages" ref={listRef}>
                {messages.slice().reverse().map((m)=> (
                  <div key={m._id || m.createdAt+String(m.sender)} className={`msg ${isMine(m)? 'mine':'theirs'}`}>
                    <div className="bubble">{m.text}</div>
                    <div className="time">
                      {new Date(m.createdAt || Date.now()).toLocaleTimeString()}
                      {isMine(m) && (
                        <span className={`ticks ${isSeen(m)? 'seen':''}`}>
                          <span className="tick">✓</span>
                          <span className="tick">✓</span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {messages.length===0 && !messagesLoading && <div className="empty">Say hi 👋</div>}
              </div>
              <div className="chat-input">
                <input
                  type="text"
                  placeholder="Message..."
                  value={input}
                  onChange={(e)=> { setInput(e.target.value); if (warn) setWarn(""); }}
                  onKeyDown={(e)=> { if (e.key==='Enter') sendMessage(); }}
                />
                <button onClick={sendMessage}>Send</button>
                {warn && <div className="warning">{warn}</div>}
                {autoDeleteMinutes && (
                  <div className="ttl-note">
                    Messages auto-delete after {autoDeleteMinutes >= 60 ? `${Math.floor(autoDeleteMinutes/60)} hour(s)` : `${autoDeleteMinutes} minute(s)`}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="placeholder">Select a conversation to start chatting</div>
          )}
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chat Auto-Delete Settings</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
            Automatically delete all chat messages (from both students and teachers) after a specified time period.
            Set to 0 or leave empty to disable.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            <TextField
              label="Delete after"
              type="number"
              value={autoDeleteValue}
              onChange={(e) => setAutoDeleteValue(e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
              inputProps={{ min: 0 }}
            />
            <TextField
              select
              label="Unit"
              value={autoDeleteUnit}
              onChange={(e) => setAutoDeleteUnit(e.target.value)}
              variant="outlined"
              size="small"
              style={{ width: 120 }}
            >
              <MenuItem value="minutes">Minutes</MenuItem>
              <MenuItem value="hours">Hours</MenuItem>
            </TextField>
          </div>
          <TextField
            label="Check interval (seconds)"
            type="number"
            value={cleanupInterval}
            onChange={(e) => setCleanupInterval(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ min: 10 }}
            helperText="How often to check for old messages (minimum 10 seconds)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={saveAutoDeleteSettings} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Doubts;
